import { useCallback, useEffect, useRef } from 'react';

import { type MessageDescriptor, type PrimitiveType, useIntl } from 'react-intl';

import { fg } from '@atlaskit/platform-feature-flags';

import type { FlagsConfig } from '../use-message-flags';

type PendingFlag = {
	description: MessageDescriptor;
	header: MessageDescriptor;
	values: Record<string, PrimitiveType> | undefined;
};

export type EmitFailureFlag = (pending: PendingFlag) => void;

/**
 * Encapsulates the "show a failure flag, re-emit it once translations finish
 * loading" logic for `NavErrorBoundary` (see NAVX-5012).
 *
 * Returns a single `emitFailureFlag(pending)` function. The caller invokes it
 * inside its error handler with the MessageDescriptor inputs; the hook handles
 * formatting, swapping the flag in-place when the inner IntlProvider re-renders
 * with the loaded messages, and dismissing the flag on unmount.
 *
 * When the `navx-4866-migrate-messages-from-nav-error-boundary` gate is OFF
 * the hook reverts to a legacy single-emission behaviour so the rollout is
 * fully reversible.
 */
export function useFailureFlagEmitter(flags: FlagsConfig | undefined): EmitFailureFlag {
	const { formatMessage } = useIntl();
	const isReemitFlagEnabled = fg('navx-4866-migrate-messages-from-nav-error-boundary');

	const pendingFlagRef = useRef<PendingFlag | null>(null);
	const dismissPendingFlagRef = useRef<(() => void) | null>(null);

	const reEmitPendingFlag = useCallback(() => {
		const pending = pendingFlagRef.current;
		if (!flags || !pending) {
			return;
		}
		dismissPendingFlagRef.current?.();
		const dismiss = flags.showFailure({
			title: formatMessage(pending.header, pending.values),
			description: formatMessage(pending.description, pending.values),
		});
		dismissPendingFlagRef.current = typeof dismiss === 'function' ? dismiss : null;
	}, [flags, formatMessage]);

	// Re-emit whenever `formatMessage` identity changes (i.e. the inner
	// IntlProvider has re-rendered with a new messages map after the async
	// load). No-op when the gate is OFF or no error has fired yet.
	useEffect(() => {
		if (!isReemitFlagEnabled) {
			return;
		}
		if (pendingFlagRef.current) {
			reEmitPendingFlag();
		}
	}, [reEmitPendingFlag, isReemitFlagEnabled]);

	// On unmount, dismiss any flag we own so we don't leak it into the host
	// app's FlagGroup after this boundary goes away.
	useEffect(() => {
		return () => {
			dismissPendingFlagRef.current?.();
			dismissPendingFlagRef.current = null;
			pendingFlagRef.current = null;
		};
	}, []);

	return useCallback(
		(pending: PendingFlag) => {
			if (isReemitFlagEnabled) {
				// Capture the descriptor inputs so the locale-change effect can
				// re-emit a re-localised flag once messages finish loading.
				// Emit immediately (possibly in the default language) so the
				// user gets visible feedback right away.
				pendingFlagRef.current = pending;
				reEmitPendingFlag();
			} else {
				// Legacy behaviour: single emission, may be in the default
				// language if the async messages loader hasn't resolved yet
				// (see NAVX-5012).
				flags?.showFailure?.({
					title: formatMessage(pending.header, pending.values),
					description: formatMessage(pending.description, pending.values),
				});
			}
		},
		[isReemitFlagEnabled, reEmitPendingFlag, flags, formatMessage],
	);
}
